
import { lazy } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import { createRoutesConfig } from "@/utils/routeUtils";
import { Loader2 } from "lucide-react";

// Lazy load admin pages
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const PropertiesManagementPage = lazy(() => import("@/pages/admin/PropertiesManagementPage"));
const PropertyDetailPage = lazy(() => import("@/pages/admin/PropertyDetailPage"));
const ExperiencesPage = lazy(() => import("@/pages/admin/ExperiencesPage"));
const PlatformManagementPage = lazy(() => import("@/pages/admin/PlatformManagementPage"));
const ContentManagementPage = lazy(() => import("@/pages/admin/ContentManagementPage"));
const SecuritySettingsPage = lazy(() => import("@/pages/admin/SecuritySettingsPage"));
const AuditLogsPage = lazy(() => import("@/pages/admin/AuditLogsPage"));
const UsersManagementPage = lazy(() => import("@/pages/admin/UsersManagementPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));
const ReportsPage = lazy(() => import("@/pages/admin/ReportsPage"));
const NotificationsPage = lazy(() => import("@/pages/admin/NotificationsPage"));
const EscrowManagementPage = lazy(() => import("@/pages/admin/EscrowManagementPage"));
const MobileMoneyPage = lazy(() => import("@/pages/admin/MobileMoneyPage"));
const AdminPaymentsPage = lazy(() => import("@/pages/admin/AdminPaymentsPage"));
const SubscriptionsPage = lazy(() => import("@/pages/admin/SubscriptionsPage"));
const TransactionsPage = lazy(() => import("@/pages/admin/TransactionsPage"));
const AdminAnalyticsPage = lazy(() => import("@/pages/admin/AdminAnalyticsPage"));
const AdminMessagesPage = lazy(() => import("@/pages/admin/AdminMessagesPage"));
const SocialMediaPage = lazy(() => import("@/pages/admin/SocialMediaPage"));
const SocialMediaAccountsPage = lazy(() => import("@/pages/admin/social-media/SocialMediaAccountsPage"));
const SocialMediaPostsPage = lazy(() => import("@/pages/admin/social-media/SocialMediaPostsPage"));
const SocialMediaCampaignsPage = lazy(() => import("@/pages/admin/social-media/SocialMediaCampaignsPage"));
const SocialMediaAnalyticsPage = lazy(() => import("@/pages/admin/social-media/SocialMediaAnalyticsPage"));
const SocialMediaInboxPage = lazy(() => import("@/pages/admin/social-media/SocialMediaInboxPage"));
// Import the OAuth callback component
import { OAuthCallback } from "@/components/admin/social-media/accounts/OAuthCallback";

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin" />
  </div>
);

// Create base admin routes configuration
const adminRoutesConfig = createRoutesConfig("/admin", <AdminLayout />, "admin");

// Define admin routes with more consistent structure
export const adminRoutes = {
  ...adminRoutesConfig,
  children: [
    { path: "", element: <AdminDashboardPage /> },
    { path: "dashboard", element: <AdminDashboardPage /> },
    { path: "properties", element: <PropertiesManagementPage /> },
    { path: "properties/:id", element: <PropertyDetailPage /> },
    { path: "experiences", element: <ExperiencesPage /> },
    { path: "platforms", element: <PlatformManagementPage /> },
    { path: "content", element: <ContentManagementPage /> },
    { path: "security", element: <SecuritySettingsPage /> },
    { path: "audit-logs", element: <AuditLogsPage /> },
    { path: "users", element: <UsersManagementPage /> },
    { path: "settings", element: <SettingsPage /> },
    { path: "reports", element: <ReportsPage /> },
    { path: "notifications", element: <NotificationsPage /> },
    { path: "escrow", element: <EscrowManagementPage /> },
    { path: "mobile-money", element: <MobileMoneyPage /> },
    { path: "payments", element: <AdminPaymentsPage /> },
    { path: "subscriptions", element: <SubscriptionsPage /> },
    { path: "transactions", element: <TransactionsPage /> },
    { path: "analytics", element: <AdminAnalyticsPage /> },
    { path: "messages", element: <AdminMessagesPage /> },
    { path: "social-media", element: <SocialMediaPage /> },
    { path: "social-media/accounts", element: <SocialMediaAccountsPage /> },
    { path: "social-media/posts", element: <SocialMediaPostsPage /> },
    { path: "social-media/campaigns", element: <SocialMediaCampaignsPage /> },
    { path: "social-media/analytics", element: <SocialMediaAnalyticsPage /> },
    { path: "social-media/inbox", element: <SocialMediaInboxPage /> },
    // Add the OAuth callback route
    { path: "auth/callback/:platform", element: <OAuthCallback /> },
  ],
};
